import { Box, Button, Container, Flex, Heading, Stack, Text } from '@chakra-ui/react'
import { css } from '@emotion/react'
import { faHeart } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from 'next/router'
import React, { useCallback, useState } from 'react'
import Masonry from 'react-masonry-css'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import logger from '@/utils/logger'
import { makeServerProps } from '@/server/query'

import { UserStoryRole } from '@/queries/constants'
import SessionFeedbackFeaturedQ from '@/queries/SessionFeedbackFeaturedQ'
import SessionFeedbackQ from '@/queries/SessionFeedbackQ'
import SessionQ from '@/queries/SessionQ'
import UserQ from '@/queries/UserQ'
import UserStoryQ from '@/queries/UserStoryQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC, { RPCRefresh } from '@/client/rpc'
import { getAddUserStoryFormInputs } from '@/client/services/formInputs'
import makePage from '@/client/services/pages/makePage'
import { getStoryAttributes, STORY_QUERY_KEY } from '@/client/services/stories'
import { getUserUrl } from '@/client/services/url'

import RequireLoginAndApproval from '@/components/Auth/RequireLoginAndApproval'
import Layout from '@/components/Layout/Layout'
import PublicImage from '@/components/PublicImage/PublicImage'
import { useInfiniteScroll } from '@/components/Session/helpers'
import ShwLink from '@/components/ShwLink'
import UserProfile from '@/components/Stories/UserProfile'
import ViewStoryModal, { type ViewStoryAttributes } from '@/components/Stories/ViewStoryModal'
import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import UserAvatar from '@/components/UserAvatar'

function AddFormFooter({ submitting }: { submitting: boolean }): React.ReactElement {
  return (
    <Stack direction="row" justifyContent="flex-end" mt={8} spacing={4}>
      <Button isLoading={submitting} type="submit">
        Submit
      </Button>
    </Stack>
  )
}

const INTIAL_LIMIT = 6

const addFormInputs: FormInputType[] = getAddUserStoryFormInputs()

const Stories: React.FC = () => {
  const toast = useToaster()
  const query = useQuery()
  const router = useRouter()

  const [limitStories, handleLoadMoreStories] = useInfiniteScroll({
    queryKey: 'limitStories',
    defaultValue: INTIAL_LIMIT,
  })

  const [limitFeedbacks, handleLoadMoreFeedbacks] = useInfiniteScroll({
    queryKey: 'limitFeedbacks',
    defaultValue: INTIAL_LIMIT,
  })

  const storyId =
    router.query[STORY_QUERY_KEY] != null
      ? Number.parseInt(`${router.query[STORY_QUERY_KEY]}`, 10)
      : null

  const stories = query.many`${UserStoryQ.where({
    order: [[UserStoryQ.created_at, 'desc']],
    published: true,
    limit: limitStories,
  })}`
  const totalStoriesCount = query<number>`COUNT(*) ${UserStoryQ.where({ published: true })}` ?? 0

  const activeStoryQuery =
    storyId != null
      ? query.one`${UserStoryQ.where({
          published: true,
          id: storyId,
        })}`
      : null

  const activeStory = activeStoryQuery != null ? getStoryAttributes(activeStoryQuery) : null

  const showStoryModal = storyId != null
  const [showAddModal, setShowAddModal] = useState(false)

  const [localClapCount, setLocalClapCount] = useState<Record<number, number>>({})

  const user = useUser()
  const me = query.one(UserQ.where({ id: user.id }))
  const meDisplayName = me(UserQ.display_name) ?? ''
  const meIsTutor = me(UserQ.hasTutorRole()) ?? false
  const meAvatarUrl = me(UserQ.avatar_url) ?? ''

  const [onClapCountChange] = useAPIWrapper(
    async ({ userStoryId }: { userStoryId: number }) => {
      await RPC.clapUserStory({
        userStoryId,
      })
      setLocalClapCount(prevState => {
        const currentClapCount = prevState[userStoryId] || 0
        return {
          ...prevState,
          [userStoryId]: currentClapCount + 1,
        }
      })
    },
    [],
    'Clap User Story'
  )

  const onSubmitStory = useCallback(
    async (values: FormValues) => {
      const role = values.role === 'learner' ? UserStoryRole.Learner : UserStoryRole.Tutor

      await RPCRefresh.submitUserStory({
        title: `${values.title}`,
        body: `${values.body}`,
        role,
      })
      toast({
        title: `Thank you for submitting! We will review your submission shortly and post if it's approved.`,
        status: 'success',
      })
      setShowAddModal(false)
    },
    [toast]
  )

  const selectStory = React.useCallback(
    (story: ViewStoryAttributes) => {
      router
        .replace({
          query: {
            ...router.query,
            [STORY_QUERY_KEY]: story.id,
          },
        })
        .catch(err => {
          logger.error('Error changing URL when selecting story modal', {
            id: story.id,
            err,
          })
        })
    },
    [router]
  )
  const handleModalDismiss = React.useCallback(() => {
    const { [STORY_QUERY_KEY]: dismissedId, ...rest } = router.query
    router
      .replace({
        query: rest,
      })
      .catch(err => {
        logger.error('Error changing URL when dismissing tutor training modal', {
          slug: dismissedId,
          err,
        })
      })
  }, [router])

  const sessionFeedbackFeatured = query.many`${SessionFeedbackFeaturedQ.where({
    onStoriesPage: true,
    include: [
      {
        association: 'SessionFeedback',
        required: true,
      },
    ],
    limit: limitFeedbacks,
  })}`

  const totalFeedbacksCount =
    query<number>`COUNT(*) ${SessionFeedbackFeaturedQ.where({ onStoriesPage: true })}` ?? 0

  return (
    <Layout title="Stories">
      <Container>
        <Box bgColor="pink.solid" width="100%" borderRadius="16px" mb="32px">
          <Flex
            maxWidth="1240px"
            width="90%"
            direction={['column', 'column', 'column', 'row', 'row']}
            margin="0 auto"
            padding={['18px 0', '32px 0', '42px 32px']}
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box maxW="600px" mb={6} position="relative" zIndex={2}>
              <Text textTransform="uppercase" color="yellow.muted" fontWeight="600" fontSize="14px">
                Community Voices
              </Text>
              <Heading
                fontSize={['5xl', '5xl', '7xl']}
                lineHeight="1.2"
                as="h1"
                color="pink.contrast"
                margin="5px 0"
              >
                Schoolhouse Stories
              </Heading>
              <Text
                fontSize={['16px', '20px', '20px']}
                lineHeight="1.4"
                fontWeight="500"
                my={4}
                color="pink.subtle"
                letterSpacing="1.2"
              >
                Schoolhouse.world is a non-profit whose mission is to connect the world through
                learning. Hear from our community about what Schoolhouse means to them!
              </Text>
              <Box my={[0, 0, 0, 4]} visibility={['hidden', 'hidden', 'hidden', 'visible']}>
                <RequireLoginAndApproval>
                  <Button
                    onClick={() => setShowAddModal(true)}
                    border="2px solid"
                    borderColor="primary.fg"
                    padding="16px 24px"
                  >
                    Share Your Story
                  </Button>
                </RequireLoginAndApproval>
              </Box>
              <Text as="span" color="pink.subtle" fontSize="14px">
                Inspired by our internet neighbor,{' '}
                <ShwLink href="https://www.khanacademy.org/stories" color="pink.contrast">
                  Khan Academy
                </ShwLink>{' '}
                <FontAwesomeIcon icon={faHeart} />
              </Text>
            </Box>
            <Box>
              <RequireLoginAndApproval>
                <Button
                  onClick={() => setShowAddModal(true)}
                  css={css`
                  height: 60px;
                  background: ${chakraTokenToVar('primary.solid')};
                  border-radius: 8px;
                  border: 4px solid ${chakraTokenToVar('primary.fg')};
                  margin: 0 auto 30px;
                  padding: 16px 24px;
                  position: relative;
                  color: ${chakraTokenToVar('primary.contrast')};
                  font-size: 24px;
                  line-height: 20px;

                  &:hover {
                    background-color: ${chakraTokenToVar('primary.fg')};
                    cursor: pointer;
                  }

                  &:active {
                    background-color: ${chakraTokenToVar('primary.fg')};
                  }

                  &::before {
                    content: '';
                    width: 0;
                    height: 0;
                    border-top: 41px solid ${chakraTokenToVar('primary.fg')};
                    border-right: 41px solid transparent;
                    position: absolute;
                    left: 11px;
                    bottom: -33px;
                    transform: scaleY(0.6);
                  }

                  &::after {
                    content: '';
                    width: 0;
                    height: 0;
                    border-top: 32px solid ${chakraTokenToVar('primary.solid')};
                    border-right: 32px solid transparent;
                    position: absolute;
                    left: 15px;
                    bottom: -24px;
                    transform: scaleY(0.6);
                  }

                  &:hover::after {
                    border-top: 32px solid ${chakraTokenToVar('primary.fg')};
                    cursor: pointer;
                  }

                  &:active::after {
                    border-top: 32px solid ${chakraTokenToVar('primary.fg')};
                  }
                `}
                >
                  &#43; Share your story
                </Button>
              </RequireLoginAndApproval>
            </Box>
          </Flex>
        </Box>
        <Masonry
          breakpointCols={{
            default: 3,
            600: 1,
          }}
          className="shw-stories"
          columnClassName="shw-stories_column"
          css={css`
            display: flex;
            margin-left: -30px; /* gutter size offset */
            width: auto;

            .shw-stories_column {
              padding-left: 32px; /* gutter size */
              background-clip: padding-box;
            }
            .shw-stories_column > div {
              margin-bottom: 48px; /* space between items */
            }
          `}
        >
          {stories.map(story => {
            const storyAttributes = getStoryAttributes(story)
            const { title, date, body, avatarUrl, name, country, role, progressionLevel } =
              storyAttributes

            return (
              <Box
                textDecoration="none"
                color="inherit"
                cursor="pointer"
                key={story(UserStoryQ.id) ?? 0}
                onClick={() => selectStory(storyAttributes)}
              >
                <Box>
                  <Flex
                    position="relative"
                    border={`2px solid ${chakraTokenToVar('border')}`}
                    borderRadius="8px"
                    css={css`
                      &::before {
                        content: '';
                        width: 0;
                        height: 0;
                        border-left: 15px solid transparent;
                        border-right: 15px solid transparent;

                        border-top: 15px solid ${chakraTokenToVar('border')};
                        position: absolute;
                        bottom: -15px;
                        left: 10px;
                        transform: scaleX(0.8);
                      }

                      &::after {
                        content: '';
                        width: 0;
                        height: 0;
                        border-left: 12px solid transparent;
                        border-right: 12px solid transparent;

                        border-top: 12px solid ${chakraTokenToVar('bg')};
                        position: absolute;
                        bottom: -12px;
                        left: 13px;
                        transform: scaleX(0.8);
                      }

                      &:hover {
                        border: 2px solid ${chakraTokenToVar('primary.border')};
                        background-color: ${chakraTokenToVar('primary.subtle')};
                      }

                      &:hover::before {
                        border-top: 15px solid ${chakraTokenToVar('primary.border')};
                      }

                      &:hover::after {
                        border-top: 12px solid ${chakraTokenToVar('bg.muted')};
                      }
                    `}
                  >
                    <Flex p="24px" flexDir="column" minWidth={0} overflow="hidden">
                      <Heading
                        size="xl"
                        as="h3"
                        mb={1}
                        noOfLines={4}
                        zIndex={2}
                        wordBreak="break-word"
                      >
                        {title}
                      </Heading>
                      <Text as="span" fontSize="14px" color="fg.subtle" fontWeight="bold" mb={3}>
                        {date}
                      </Text>
                      <Text noOfLines={3} wordBreak="break-word">
                        {body}
                      </Text>
                    </Flex>
                    <Box position="absolute" top="40px" left="10px" zIndex={1}>
                      <Text fontSize="100px" fontWeight="bold" color="yellow.emphasized">
                        “
                      </Text>
                    </Box>
                  </Flex>
                </Box>
                <Stack mt="24px" mb="24px" align="center" direction="row" spacing={2}>
                  <UserAvatar
                    src={avatarUrl}
                    name={name}
                    size="lg"
                    progressionLevel={progressionLevel}
                  />
                  <Box>
                    <Text fontSize="20px" fontWeight="600">
                      {name}
                    </Text>
                    <Box fontSize="14px" color="fg.muted">
                      <Box as="span" fontWeight="600">
                        {role}
                      </Box>{' '}
                      in{' '}
                      <Box as="span" fontWeight="600">
                        {country}
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            )
          })}
        </Masonry>

        {totalStoriesCount > limitStories ? (
          <Flex justifyContent="center">
            <Button minWidth="150px" onClick={handleLoadMoreStories}>
              See More
            </Button>
          </Flex>
        ) : null}

        <Stack align="center" direction="row" mt="64px" mb={5} spacing={1}>
          <Box borderTop={`2px solid ${chakraTokenToVar('border')}`} flex={1} />
          <Text variant="eyebrow" flex={1} flexShrink={0} textAlign="center">
            What Learners Have To Say About Sessions
          </Text>
          <Box borderTop={`2px solid ${chakraTokenToVar('border')}`} flex={1} />
        </Stack>
        <Masonry
          breakpointCols={2}
          className="shw-stories-feedback"
          columnClassName="shw-stories-feedback_column"
          css={css`
            display: flex;
            margin-left: -30px; /* gutter size offset */
            width: auto;

            .shw-stories-feedback_column {
              padding-left: 32px; /* gutter size */
              background-clip: padding-box;
            }
            .shw-stories-feedback_column > div {
              margin-bottom: 32px; /* space between items */
            }
          `}
        >
          {sessionFeedbackFeatured.map(feedbackFeatured => {
            const feedbackComment = feedbackFeatured(SessionFeedbackQ.comment)
            const session = feedbackFeatured.one(
              SessionQ.where({
                id: SessionFeedbackQ.session_id,
                include: [{ association: 'User', required: true }],
              })
            )
            const userDisplayName = session(UserQ.display_name)

            const tutorId = session(SessionQ.user_id) ?? 0
            const tutorUsername = session(UserQ.username)

            return (
              <Box flex={1} key={feedbackFeatured(SessionFeedbackQ.id) ?? 0}>
                <Box
                  position="relative"
                  rounded="md"
                  fontWeight="bold"
                  border={`2px solid ${chakraTokenToVar('border')}`}
                  my={4}
                  p={4}
                  minH="100px"
                >
                  <Box mt={3} ml={3}>
                    {feedbackComment}
                  </Box>

                  <Box mt={3} ml={3} color="fg.muted">
                    <Box as="span" fontWeight="600">
                      Learner
                    </Box>{' '}
                    in{' '}
                    <ShwLink
                      variant="unstyled"
                      textDecoration="underline"
                      href={getUserUrl({ userId: tutorId, username: tutorUsername })}
                    >
                      <Box as="span" fontWeight="600">
                        {userDisplayName}
                      </Box>
                    </ShwLink>
                    &apos;s session
                  </Box>

                  <Box position="absolute" top="40px" zIndex={-1}>
                    <Text fontSize="100px" fontWeight="bold" color="yellow.emphasized">
                      “
                    </Text>
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Masonry>

        {totalFeedbacksCount > limitFeedbacks ? (
          <Flex justifyContent="center">
            <Button minWidth="150px" onClick={handleLoadMoreFeedbacks}>
              See More
            </Button>
          </Flex>
        ) : null}

        <Box my="36px" borderTop={`2px solid ${chakraTokenToVar('border')}`} flex={1} />
        <Box textAlign="center" mt="36px" mb="64px">
          <PublicImage
            src="/stories-cta.svg"
            alt="Share how Schoolhouse has impacted you."
            mb={4}
          />
          <Heading size="2xl" mb={4}>
            Have a story to share?
          </Heading>
          <Text mb={4}>
            Let us know how Schoolhouse.world impacted you to have a chance to be featured on our
            site.
          </Text>
          <RequireLoginAndApproval>
            <Button onClick={() => setShowAddModal(true)}>Share Your Story</Button>
          </RequireLoginAndApproval>
        </Box>
      </Container>
      <ViewStoryModal
        story={activeStory}
        showStoryModal={showStoryModal}
        storyClapCount={(activeStory?.clapCount ?? 0) + (localClapCount[activeStory?.id ?? 0] ?? 0)}
        onClose={handleModalDismiss}
        onClapCountChange={() =>
          onClapCountChange({
            userStoryId: activeStory !== null ? activeStory.id : 0,
          })
        }
      />
      <ModalV3
        size="3xl"
        title="Submit your story"
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        body={() => (
          <>
            <UserProfile
              avatarUrl={meAvatarUrl}
              name={meDisplayName}
              role={meIsTutor ? 'Tutor' : 'Learner'}
            />
            <Form
              name="Submit a Story"
              FooterComponent={AddFormFooter}
              blocks={addFormInputs}
              onSubmit={onSubmitStory}
            />
          </>
        )}
      />
    </Layout>
  )
}

const StoriesPage = makePage(Stories, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(StoriesPage)
export default StoriesPage
